import React, { useState, useEffect, useCallback } from "react";
import { Search, Loader2, Check, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Avatar,
  AvatarFallback,
  Card,
  CardContent,
} from "@b2b/ui-components";
import { adminService } from "@b2b/services";

interface Seller {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  company?: {
    name?: string;
  };
}

interface SellerSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedSellerId?: string;
  onSelect: (sellerId: string, seller: Seller) => void;
  companyId?: string;
}

export function SellerSelectionDialog({
  open,
  onOpenChange,
  selectedSellerId,
  onSelect,
  companyId,
}: SellerSelectionDialogProps) {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [tempSelectedSellerId, setTempSelectedSellerId] = useState<string | undefined>(selectedSellerId);
  const limit = 10;

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on new search
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch sellers
  const fetchSellers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await adminService.getSellers({
        page,
        limit,
        search: debouncedSearch || undefined,
        status: "APPROVED", // Only show approved sellers
      });

      if (response.sellers) {
        // Map PendingUser[] to Seller[] by converting null to undefined
        const mappedSellers: Seller[] = response.sellers.map((seller) => ({
          id: seller.id,
          firstName: seller.firstName ?? undefined,
          lastName: seller.lastName ?? undefined,
          email: seller.email,
          company: seller.company ? {
            name: seller.company.name,
          } : undefined,
        }));
        setSellers(mappedSellers);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotal(response.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch sellers:", error);
      setSellers([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  // Fetch sellers when dialog opens or search/pagination changes
  useEffect(() => {
    if (open) {
      fetchSellers();
    }
  }, [open, fetchSellers]);

  // Reset temp selection when dialog opens
  useEffect(() => {
    if (open) {
      setTempSelectedSellerId(selectedSellerId);
      setSearchQuery("");
    }
  }, [open, selectedSellerId]);

  const handleSelect = (seller: Seller) => {
    setTempSelectedSellerId(seller.id);
  };

  const handleConfirm = () => {
    if (tempSelectedSellerId) {
      const selectedSeller = sellers.find((s) => s.id === tempSelectedSellerId);
      if (selectedSeller) {
        onSelect(tempSelectedSellerId, selectedSeller);
      }
    }
    onOpenChange(false);
  };

  const handleCancel = () => {
    setTempSelectedSellerId(selectedSellerId);
    setSearchQuery("");
    onOpenChange(false);
  };

  const getSellerDisplayName = (seller: Seller) => {
    const firstName = seller.firstName || "";
    const lastName = seller.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || seller.email || "Unknown Seller";
  };

  const getSellerInitials = (seller: Seller) => {
    const firstName = seller.firstName || "";
    const lastName = seller.lastName || "";
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (seller.email) {
      return seller.email[0].toUpperCase();
    }
    return "U";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] flex flex-col bg-white border-2 border-orange-200 shadow-2xl overflow-hidden"
        closeButtonClassName="z-20 top-2 right-2 sm:top-4 sm:right-4 flex size-11 min-h-11 min-w-11 items-center justify-center rounded-md sm:size-9 sm:min-h-9 sm:min-w-9"
      >
        <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-gradient-to-r from-orange-50 to-orange-100 border-b border-orange-200 px-6 pb-6 pt-14 sm:pt-16">
          <DialogTitle className="text-xl font-semibold ">
            Select Seller
          </DialogTitle>
          <p className="text-sm  mt-1">
            Search and select an approved seller to assign this product
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col space-y-4 p-6">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-orange-500 z-10" />
            <Input
              placeholder="Search by name, email, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-orange-200 focus:border-orange-500 focus:ring-orange-500 transition-all duration-200"
            />
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
              <span className="ml-2 text-sm text-orange-700 font-medium">Loading sellers...</span>
            </div>
          )}

          {/* Seller List */}
          {!isLoading && (
            <div className="flex-1 overflow-y-auto space-y-3 min-h-0 pr-2 scroll-smooth">
              {sellers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <User className="h-16 w-16 text-orange-200 mb-4" />
                  <p className="text-sm font-medium text-orange-700">
                    {debouncedSearch
                      ? "No sellers found matching your search."
                      : "No approved sellers available."}
                  </p>
                  {debouncedSearch && (
                    <p className="text-xs text-orange-600 mt-1">
                      Try a different search term
                    </p>
                  )}
                </div>
              ) : (
                sellers.map((seller) => {
                  const isSelected = tempSelectedSellerId === seller.id;
                  return (
                    <Card
                      key={seller.id}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-lg transform hover:scale-[1.01] ${
                        isSelected
                          ? "border-2 border-orange-500 bg-orange-50 shadow-md"
                          : "border border-gray-200 hover:border-orange-300 bg-white"
                      }`}
                      onClick={() => handleSelect(seller)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <Avatar className={`h-12 w-12 transition-all duration-200 ${
                            isSelected ? 'ring-2 ring-orange-500 ring-offset-2' : ''
                          }`}
                          >
                            <AvatarFallback className={`transition-colors duration-200 ${
                              isSelected
                                ? "bg-orange-500 text-white"
                                : "bg-orange-100 text-orange-700"
                            }`}>
                              {getSellerInitials(seller)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`font-semibold truncate transition-colors duration-200 ${
                                isSelected ? "text-orange-900" : "text-gray-900"
                              }`}>
                                {getSellerDisplayName(seller)}
                              </p>
                              {isSelected && (
                                <div className="flex-shrink-0 bg-orange-500 rounded-full p-1">
                                  <Check className="h-4 w-4 text-white" />
                                </div>
                              )}
                            </div>
                            <p className={`text-sm truncate mt-1 transition-colors duration-200 ${
                              isSelected ? "text-orange-700" : "text-gray-600"
                            }`}>
                              {seller.email}
                            </p>
                            {seller.company?.name && (
                              <p className={`text-sm truncate mt-0.5 transition-colors duration-200 ${
                                isSelected ? "text-orange-600" : "text-gray-500"
                              }`}>
                                {seller.company.name}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          )}

          {/* Pagination */}
          {!isLoading && totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-orange-200">
              <div className="text-sm font-medium text-black">
                Showing {sellers.length > 0 ? (page - 1) * limit + 1 : 0} -{" "}
                {Math.min(page * limit, total)} of {total} sellers
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="border-orange-200 text-orange-700 hover:bg-orange-50 hover:border-orange-300 transition-all duration-200 disabled:opacity-50"
                >
                  Previous
                </Button>
                <span className="text-sm font-medium text-black px-3">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="border-orange-200 text-orange-700 hover:bg-orange-50 hover:border-orange-300 transition-all duration-200 disabled:opacity-50"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="border-t border-orange-200 bg-orange-50/50 p-6">
          <Button 
            variant="outline" 
            onClick={handleCancel}
            className="border-orange-200 text-orange-700 hover:bg-white hover:border-orange-300 transition-all duration-200"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!tempSelectedSellerId}
            className="bg-orange-500 hover:bg-orange-600 text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
          >
            Confirm Selection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

